import React, { useState, useEffect, useRef } from 'react';
import invoiceService from 'services/invoiceService';
import Swal from 'sweetalert2';
import 'styles/manager.css';

export const ApprovalsView = ({ onApprove, onReject, onRequestChanges }) => {
  const [activeFilter, setActiveFilter] = useState('all');
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  // Solo la primera carga muestra el spinner de pantalla completa
  const [initialLoad, setInitialLoad] = useState(true);
  // Evita que una respuesta vieja sobrescriba una más reciente
  const requestIdRef = useRef(0);

  // ── Búsqueda con debounce (mismo mecanismo que InvoicesView) ─────────────
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({
    page: 1, limit: 10, totalCount: 0, totalPages: 1, hasNext: false, hasPrev: false,
  });

  // Dispara carga cuando cambia filtro o búsqueda debounced
  useEffect(() => { loadPendingInvoices(1); }, [activeFilter, debouncedSearch]);

  // Debounce: espera 500ms tras el último teclazo, luego actualiza debouncedSearch y resetea página
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setCurrentPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const loadPendingInvoices = async (page = currentPage) => {
    const requestId = ++requestIdRef.current;
    try {
      setLoading(true);
      const params = {
        page,
        limit: pagination.limit,
        estatus: activeFilter === 'all' ? 'recibida,en_revision,aprobada' : activeFilter,
      };

      // Búsqueda backend (igual que InvoicesView)
      if (debouncedSearch.trim()) {
        params.search = debouncedSearch.trim();
      }

      const response = await invoiceService.getInvoices(params);
      if (requestId !== requestIdRef.current) return; // respuesta vieja
      if (!response.error) {
        setInvoices(response.data.data || []);
        setPagination(response.data.pagination);
        setCurrentPage(response.data.pagination.page);
      }
    } catch {
      Swal.fire({ icon: 'error', title: 'Error', text: 'No se pudieron cargar las facturas pendientes', timer: 3000, showConfirmButton: false });
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
        setInitialLoad(false);
      }
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > pagination.totalPages) return;
    loadPendingInvoices(newPage);
  };

  const formatDate = (s) => {
    if (!s) return '—';
    return new Date(s).toLocaleDateString('es-MX', { year: 'numeric', month: 'long', day: 'numeric' });
  };
  const formatCurrency = (n) =>
    new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(n || 0);

  const handleViewPaymentProof = async (invoiceId) => {
    const result = await invoiceService.downloadInvoiceFile(invoiceId, 'comprobante');
    if (result.error) {
      Swal.fire({ icon: 'error', title: 'Error', text: 'No se pudo abrir el comprobante de pago', timer: 3000, showConfirmButton: false });
    }
  };

  const statusTranslations = {
    recibida: 'Recibida', en_revision: 'En Revisión', aprobada: 'Aprobada',
    rechazada: 'Rechazada', pagada: 'Pagada',
  };

  const filters = [
    { id: 'all', label: 'Todas pendientes' },
    { id: 'recibida', label: 'Pendientes' },
    { id: 'en_revision', label: 'En Revisión' },
    { id: 'aprobada', label: 'Aprobadas' },
    { id: 'pagada', label: 'Pagadas' },
  ];

  // ── 1. Ver detalles ─────────────────────────────────────────────────────────
  const handleViewDetails = async (invoice) => {
    const details = await invoiceService.getInvoiceById(invoice.id);
    const viewUrl = invoiceService.viewInvoiceFileUrl(invoice.id, 'firmado');

    const inv = details.error ? invoice : (details.data.data || invoice);
    // Página de detalle a pantalla completa (se abre en otra pestaña)
    const detailUrl = `/invoice-detail/${invoice.id}`;
    const verMasBtn = `
      <a href="${detailUrl}" target="_blank" rel="noopener noreferrer"
         style="display:inline-flex;align-items:center;gap:6px;padding:8px 16px;border-radius:8px;
                background:#8b6f47;color:#fff;font-weight:600;text-decoration:none;font-size:.9rem">
        <i class="bi bi-box-arrow-up-right"></i> Ver más
      </a>`;

    const firmadaLink = inv.factura_firmada_url
      ? `<p><strong>Factura Firmada:</strong> <a href="${inv.factura_firmada_url}" target="_blank" style="color:#007bff">📄 Ver documento soporte</a></p>`
      : '<p><strong>Factura Firmada:</strong> No disponible</p>';
    const ppd = inv.metodo_pago
      ? `<p><strong>Método de Pago SAT:</strong> <strong>${inv.metodo_pago}</strong></p>` : '';

    Swal.fire({
      title: `Factura ${inv.folio}`,
      html: `
        <div style="text-align:right;margin-bottom:10px">${verMasBtn}</div>
        <div style="text-align:left;max-height:480px;overflow-y:auto;padding-right:4px">
          <p><strong>UUID:</strong> <span style="font-family:monospace;font-size:.85rem">${inv.uuid}</span></p>
          <p><strong>Proveedor:</strong> ${inv.proveedor_nombre || 'N/A'}</p>
          <p><strong>RFC Emisor:</strong> ${inv.rfc_emisor}</p>
          <p><strong>RFC Receptor:</strong> ${inv.rfc_receptor}</p>
          <p><strong>Método de Pago:</strong> ${inv.datos_xml?.metodoPago || 'N/A'}</p>
          <p><strong>Fecha:</strong> ${formatDate(inv.fecha)}</p>
          ${ppd}
          <p><strong>Subtotal:</strong> ${formatCurrency(inv.subtotal)}</p>
          <p><strong>IVA:</strong> ${formatCurrency(inv.iva)}</p>
          <p><strong>Total:</strong> ${formatCurrency(inv.total)}</p>
          <p><strong>Centro de Costo:</strong> ${inv.centro_costo_nombre || 'N/A'}</p>
          <p><strong>Tipo de Gasto:</strong> ${inv.tipo_gasto_nombre || 'N/A'}</p>
          ${inv.fecha_pago_programada ? `<p><strong>Pago programado:</strong> ${formatDate(inv.fecha_pago_programada)}</p>` : ''}
          ${inv.notas ? `<p><strong>Notas:</strong> ${inv.notas}</p>` : ''}
          ${inv.notas_rechazo ? `<p style="color:#dc3545"><strong>Notas de Rechazo:</strong> ${inv.notas_rechazo}</p>` : ''}
          <hr style="margin:10px 0"/>
          <p style="font-weight:700;margin-bottom:6px">📎 Documentos</p>
          ${firmadaLink}
          <iframe
              src="${viewUrl}"
              width="100%"
              height="600px"
              title="Vista factura"
            ></iframe>
        </div>`,
      footer: `<span style="color:#6c757d;font-size:.85rem">¿Necesitas hacer zoom?</span>&nbsp;${verMasBtn}`,
      width: 640,
      confirmButtonText: 'Cerrar',
    });
  };

  // ── 2. Aprobar (primero en_revision, luego aprobada) ───────────────────────
  const handleApprove = async (invoice) => {
    console.log(invoice)
    const suggestedDate = new Date();
    suggestedDate.setDate(suggestedDate.getDate() + 15);
    const suggestedStr = suggestedDate.toISOString().split('T')[0];

    const result = await Swal.fire({
      title: '¿Aprobar factura?',
      html: `
        <div style="text-align:left">
          <p><strong>Folio:</strong> ${invoice.folio}</p>
          <p><strong>Proveedor:</strong> ${invoice.proveedor_nombre}</p>
          <p><strong>Monto:</strong> ${formatCurrency(invoice.total)}</p>
          ${invoice.metodo_pago ? `<p><strong>Método de Pago SAT:</strong> <strong>${invoice.metodo_pago}</strong></p>` : ''}
          <hr style="margin:12px 0"/>
          <div style="margin-bottom:12px">
            <label style="display:block;margin-bottom:5px"><strong>Fecha de pago programada:</strong></label>
            <input id="swal-approve-date" type="date" class="swal2-input" value="${suggestedStr}" style="margin:0;width:100%">
            <small style="color:#6c757d;display:block;margin-top:4px">Puedes modificar la fecha si lo requieres</small>
          </div>
        </div>`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Sí, aprobar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#28a745',
      preConfirm: () => {
        const d = document.getElementById('swal-approve-date').value;
        if (!d) { Swal.showValidationMessage('Selecciona una fecha de pago'); return false; }
        return d;
      },
    });

    if (!result.isConfirmed) return;

    try {
      // Paso 1: marcar en revisión
      //invoice.estatus=="en_revision"
      if(invoice.estatus!="en_revision"){
      const reviewRes = await invoiceService.updateInvoiceStatus(invoice.id, { estatus: 'en_revision' });
      if (reviewRes.error) throw new Error('en_revision');
      }
      // Paso 2: aprobar con fecha de pago
      const approveRes = await invoiceService.updateInvoiceStatus(invoice.id, {
        estatus: 'aprobada',
        fecha_pago_programada: result.value,
      });
      if (!approveRes.error) {
        Swal.fire({
          icon: 'success',
          title: '¡Factura aprobada!',
          html: `<p>La factura <strong>${invoice.folio}</strong> fue aprobada.</p><p>Pago programado: <strong>${formatDate(result.value)}</strong></p>`,
          timer: 3500,
          showConfirmButton: false,
        });
        loadPendingInvoices(currentPage);
        if (onApprove) onApprove(invoice.id);
      }
    } catch {
      Swal.fire({ icon: 'error', title: 'Error', text: 'No se pudo aprobar la factura', timer: 3000, showConfirmButton: false });
    }
  };

  // ── 4. Rechazar ─────────────────────────────────────────────────────────────
  const handleReject = async (invoice) => {
    const result = await Swal.fire({
      title: '¿Rechazar factura?',
      html: `
        <div style="text-align:left">
          <p><strong>Folio:</strong> ${invoice.folio}</p>
          <p><strong>Proveedor:</strong> ${invoice.proveedor_nombre}</p>
          <p><strong>Monto:</strong> ${formatCurrency(invoice.total)}</p>
          <hr style="margin:12px 0"/>
          <div style="margin-bottom:12px">
            <label style="display:block;margin-bottom:5px"><strong>Motivo del rechazo:</strong> <span style="color:#dc3545">*</span></label>
            <textarea id="swal-reject-notes" class="swal2-input" placeholder="Describe el motivo para notificar al proveedor..." rows="3" style="width:100%;box-sizing:border-box;height:80px;resize:vertical"></textarea>
          </div>
        </div>`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sí, rechazar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#dc3545',
      preConfirm: () => {
        const notes = document.getElementById('swal-reject-notes').value;
        if (!notes?.trim()) { Swal.showValidationMessage('Debes especificar el motivo del rechazo'); return false; }
        return notes;
      },
    });

    if (!result.isConfirmed) return;

    try {
      const response = await invoiceService.updateInvoiceStatus(invoice.id, { estatus: 'rechazada', notas_rechazo: result.value });
      if (!response.error) {
        Swal.fire({ icon: 'success', title: 'Factura rechazada', text: `La factura ${invoice.folio} fue rechazada.`, timer: 3000, showConfirmButton: false });
        loadPendingInvoices(currentPage);
        if (onReject) onReject(invoice.id);
      }
    } catch {
      Swal.fire({ icon: 'error', title: 'Error', text: 'No se pudo rechazar la factura', timer: 3000, showConfirmButton: false });
    }
  };

  // ── 5. Cambiar fecha de pago ─────────────────────────────────────────────────
  const handleChangeDateModal = async (invoice) => {
    const currentDate = invoice.fecha_pago_programada
      ? invoice.fecha_pago_programada.split('T')[0] : '';

    const result = await Swal.fire({
      title: '📅 Cambiar Fecha de Pago',
      html: `
        <div style="text-align:left">
          <p><strong>Folio:</strong> ${invoice.folio}</p>
          <p><strong>Proveedor:</strong> ${invoice.proveedor_nombre}</p>
          ${currentDate ? `<p><strong>Fecha actual:</strong> ${formatDate(currentDate)}</p>` : ''}
          <hr style="margin:12px 0"/>
          <div style="margin-bottom:12px">
            <label style="display:block;margin-bottom:5px"><strong>Nueva fecha de pago:</strong></label>
            <input id="swal-new-date" type="date" class="swal2-input" value="${currentDate}" style="margin:0;width:100%">
          </div>
        </div>`,
      showCancelButton: true,
      confirmButtonText: 'Guardar fecha',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#6f42c1',
      preConfirm: () => {
        const d = document.getElementById('swal-new-date').value;
        if (!d) { Swal.showValidationMessage('Selecciona una fecha'); return false; }
        return d;
      },
    });

    if (!result.isConfirmed) return;

    try {
      const res = await invoiceService.updateInvoiceStatus(invoice.id, { fecha_pago_programada: result.value });
      if (!res.error) {
        Swal.fire({ icon: 'success', title: 'Fecha actualizada', text: `Nueva fecha de pago: ${formatDate(result.value)}`, timer: 2500, showConfirmButton: false });
        loadPendingInvoices(currentPage);
      }
    } catch {
      Swal.fire({ icon: 'error', title: 'Error', text: 'No se pudo actualizar la fecha', timer: 3000, showConfirmButton: false });
    }
  };

  // ── 6. Subir comprobante de pago ────────────────────────────────────────────
  const handleUploadPayment = async (invoice) => {
    const isPPD = invoice?.metodo_pago === 'PPD';
    const isEditing = invoice?.estatus === 'pagada';
    const todayStr = new Date().toISOString().split('T')[0];
    const paymentDate = invoice.fecha_pago_real
      ? invoice.fecha_pago_real.split('T')[0]
      : todayStr;
    const result = await Swal.fire({
      title: isEditing ? 'Editar Comprobante de Pago' : '💳 Registrar Pago',
      html: `
        <div style="text-align:left">
          <p><strong>Folio:</strong> ${invoice.folio}</p>
          <p><strong>Proveedor:</strong> ${invoice.proveedor_nombre}</p>
          <p><strong>Monto:</strong> ${formatCurrency(invoice.total)}</p>
          ${invoice.comprobante_pago_url ? `<p><strong>Comprobante actual:</strong> <a href="${invoice.comprobante_pago_url}" target="_blank" rel="noopener noreferrer">Ver comprobante</a></p>` : ''}
          ${invoice.metodo_pago ? `<p><strong>Método SAT:</strong> <strong style="color:${isPPD ? '#856404' : '#155724'}">${invoice.metodo_pago}</strong></p>` : ''}
          ${isPPD ? `
          <div style="background:#fff3cd;border:1px solid #ffc107;border-radius:8px;padding:10px 14px;margin:10px 0;font-size:.875rem;color:#856404;display:flex;gap:8px">
            <span>📋</span>
            <span>Al confirmar el pago, se enviará un correo al proveedor con <strong>7 días naturales</strong> para subir el Complemento de Pago.</span>
          </div>` : ''}
          <hr style="margin:12px 0"/>
          <div style="margin-bottom:14px">
            <label style="display:block;margin-bottom:5px"><strong>Fecha de pago real:</strong></label>
            <input id="swal-payment-date" type="date" class="swal2-input" value="${paymentDate}" style="margin:0;width:100%">
          </div>
          <div style="margin-bottom:12px">
            <label style="display:block;margin-bottom:5px"><strong>Comprobante de pago:</strong> <span style="color:#dc3545">*</span></label>
            <input id="swal-payment-file" type="file" accept=".pdf,.jpg,.jpeg,.png" class="swal2-input" style="margin:0;width:100%;padding:8px">
            <small style="color:#6c757d;display:block;margin-top:4px">PDF, JPG o PNG — Máx 10 MB</small>
          </div>
        </div>`,
      showCancelButton: true,
      confirmButtonText: isEditing ? 'Guardar cambios' : '✓ Confirmar Pago',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#28a745',
      preConfirm: () => {
        const fileInput = document.getElementById('swal-payment-file');
        const dateInput = document.getElementById('swal-payment-date');
        if (!fileInput.files[0]) { Swal.showValidationMessage('Selecciona el comprobante de pago'); return false; }
        if (!dateInput.value) { Swal.showValidationMessage('Selecciona la fecha de pago'); return false; }
        return { file: fileInput.files[0], date: dateInput.value };
      },
    });

    if (!result.isConfirmed) return;

    try {
      const fd = new FormData();
      fd.append('comprobante', result.value.file);
      fd.append('fecha_pago_real', result.value.date);
      const res = await invoiceService.uploadPaymentProof(invoice.id, fd);
      if (!res.error) {
        const msg = isEditing
          ? 'El comprobante de pago fue actualizado.'
          : isPPD
          ? 'Factura marcada como pagada. El proveedor tiene 7 días para subir el Complemento de Pago.'
          : 'La factura ha sido marcada como pagada y se notificó al proveedor.';
        Swal.fire({ icon: 'success', title: isEditing ? 'Comprobante actualizado' : '¡Pago registrado!', text: msg, confirmButtonText: 'OK' });
        loadPendingInvoices(currentPage);
      } else {
        throw new Error('Error al registrar pago');
      }
    } catch {
      Swal.fire({ icon: 'error', title: 'Error', text: 'No se pudo registrar el comprobante de pago', timer: 3000, showConfirmButton: false });
    }
  };

  // ── Render páginas ──────────────────────────────────────────────────────────
  const renderPageNumbers = () => {
    const { totalPages } = pagination;
    const pages = [];
    let start = Math.max(1, currentPage - 2);
    let end = Math.min(totalPages, start + 4);
    if (end - start < 4) start = Math.max(1, end - 4);
    for (let i = start; i <= end; i++) {
      pages.push(
        <button key={i} className={`pagination-btn ${i === currentPage ? 'pagination-btn--active' : ''}`} onClick={() => handlePageChange(i)}>
          {i}
        </button>
      );
    }
    return pages;
  };

  if (initialLoad) {
    return (
      <div className="approvals-view">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Cargando facturas...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="approvals-view">
      <div className="card">
        <div className="card-header">Revisión y Aprobación de Facturas</div>

        {/* ── Barra de búsqueda (mismo patrón que InvoicesView) ── */}
        <div className="search-and-filter">
          <input
            type="text"
            className="search-box"
            placeholder="Buscar por folio, UUID, monto o proveedor..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        {/* ── Filtros de estatus ── */}
        <div className="filter-group">
          {filters.map((f) => (
            <button
              key={f.id}
              className={`filter-btn ${activeFilter === f.id ? 'active' : ''}`}
              onClick={() => {
                setActiveFilter(f.id);
                setCurrentPage(1);
              }}
            >
              {f.label}
            </button>
          ))}
        </div>

        <div className="pending-invoices-list" style={{ opacity: loading ? 0.5 : 1, pointerEvents: loading ? 'none' : 'auto', transition: 'opacity 0.2s' }}>
          {invoices.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">✓</div>
              <div className="empty-text">
                {searchTerm ? 'No se encontraron facturas' : 'No hay facturas pendientes en este filtro'}
              </div>
            </div>
          ) : (
            invoices.map((invoice) => (
              <div key={invoice.id} className="pending-invoice-card">
                <div className="invoice-content">
                  <div className="invoice-header-row">
                    <div>
                      <h3 className="invoice-folio">{invoice.folio}</h3>
                      <p className="invoice-provider">{invoice.proveedor_nombre}</p>
                      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center', marginTop: '6px' }}>
                        <span className={`status-badge status-${invoice.estatus}`}>
                          {statusTranslations[invoice.estatus]}
                        </span>
                        {invoice.metodo_pago && (
                          <span style={{
                            display: 'inline-block', fontSize: '0.75rem', fontWeight: 700, padding: '2px 8px', borderRadius: '4px',
                            background: invoice.metodo_pago === 'PPD' ? '#fff3cd' : '#d4edda',
                            color: invoice.metodo_pago === 'PPD' ? '#856404' : '#155724',
                            border: `1px solid ${invoice.metodo_pago === 'PPD' ? '#ffc107' : '#28a745'}`,
                          }}>
                            SAT: {invoice.metodo_pago}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="invoice-amount-section">
                      <div className="invoice-amount">{formatCurrency(invoice.total)}</div>
                      <div className="invoice-date">{formatDate(invoice.fecha)}</div>
                      {invoice.fecha_pago_programada && (
                        <div style={{ fontSize: '0.8rem', color: '#6c757d', marginTop: '4px' }}>
                          Pago: {formatDate(invoice.fecha_pago_programada)}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="invoice-description-box">
                    <div className="description-title">Centro de Costo / Tipo de Gasto</div>
                    <p className="description-text">{invoice.centro_costo_nombre || 'N/A'} / {invoice.tipo_gasto_nombre || 'N/A'}</p>
                    <div className="uuid-section">
                      <strong>UUID:</strong> <span className="uuid-text">{invoice.uuid}</span>
                    </div>
                  </div>

                  {invoice.metodo_pago === 'PPD' && invoice.estatus !== 'pagada' && (
                    <div style={{ background: '#fff3cd', border: '1px solid #ffc107', borderRadius: '8px', padding: '10px 14px', marginTop: '10px', fontSize: '0.875rem', color: '#856404', display: 'flex', gap: '8px' }}>
                      <span>⚠️</span>
                      <span>Esta factura tiene método de pago <strong>PPD</strong>. Al confirmar el pago, el proveedor tendrá 7 días para subir el Complemento de Pago.</span>
                    </div>
                  )}

                  <div className="validations-box" style={{ marginTop: '10px' }}>
                    <div className="validations-title">✓ Validaciones Aprobadas</div>
                    <div className="validations-items">
                      <div className="validation-check">✓ RFC Válido</div>
                      <div className="validation-check">✓ Montos Correctos</div>
                      <div className="validation-check">✓ UUID Verificado</div>
                      <div className="validation-check">✓ Sin Duplicados</div>
                    </div>
                  </div>
                </div>

                {/* ── Botones de acción ── */}
                <div className="invoice-actions" style={{ flexWrap: 'wrap', gap: '8px' }}>
                  {(invoice.estatus === 'recibida' || invoice.estatus === 'en_revision') && (
                    <>
                      <button className="btn btn-approve" onClick={() => handleApprove(invoice)}>✓ Aprobar</button>
                      <button className="btn btn-reject" onClick={() => handleReject(invoice)}>✗ Rechazar</button>
                    </>
                  )}
                  {invoice.estatus === 'aprobada' && (
                    <>
                      <button className="btn" style={{ background: '#6f42c1', color: 'white' }} onClick={() => handleChangeDateModal(invoice)}>
                        📅 Cambiar Fecha
                      </button>
                      <button className="btn" style={{ background: '#28a745', color: 'white' }} onClick={() => handleUploadPayment(invoice)}>
                        💳 Subir Comprobante
                      </button>
                    </>
                  )}
                  {invoice.estatus === 'pagada' && (
                    <>
                      {(invoice.comprobante_pago || invoice.comprobante_pago_url) && (
                        <button className="btn" style={{ background: '#17a2b8', color: 'white' }} onClick={() => handleViewPaymentProof(invoice.id)}>
                          Ver Comprobante
                        </button>
                      )}
                      <button className="btn" style={{ background: '#28a745', color: 'white' }} onClick={() => handleUploadPayment(invoice)}>
                        Editar Comprobante
                      </button>
                    </>
                  )}
                  <button className="btn btn-view" onClick={() => handleViewDetails(invoice)}>👁️ Ver Detalles</button>
                </div>
              </div>
            ))
          )}
        </div>

        {/* ── Paginación ── */}
        {pagination.totalPages > 1 && (
          <div className="pagination">
            <div className="pagination-info">
              Mostrando {invoices.length} de {pagination.totalCount} facturas
            </div>
            <div className="pagination-controls">
              <button className="pagination-btn" onClick={() => handlePageChange(1)} disabled={!pagination.hasPrev}>«</button>
              <button className="pagination-btn" onClick={() => handlePageChange(currentPage - 1)} disabled={!pagination.hasPrev}>‹</button>
              {renderPageNumbers()}
              <button className="pagination-btn" onClick={() => handlePageChange(currentPage + 1)} disabled={!pagination.hasNext}>›</button>
              <button className="pagination-btn" onClick={() => handlePageChange(pagination.totalPages)} disabled={!pagination.hasNext}>»</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};